import { ConfirmationModal, useModals } from "@skailer-com/uikit";
import { GoalConfirmationDeleteWithSubGoalsModal } from "@/pages/talent/goals/widgets/GoalConfirmationDeleteWithSubGoalsModal.tsx";
import { GoalEditModal } from "@/pages/talent/goals/widgets/GoalEditModal.tsx";
import { goalsKeys, useGoalDelete } from "@/queries/goals.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiGoal, ApiGoalSearchFilter } from "@/types/goals.types.ts";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export interface FlatGoalRow {
  goal: ApiGoal;
  depth: number;
  path: string;
}

export interface UseGoalsTableStateResult {
  expandedPaths: Set<string>;
  loadingPaths: Set<string>;
  childrenMap: Record<number, ApiGoal[]>;
  flatGoals: FlatGoalRow[];
  toggleExpand: (goalId: number, path: string) => Promise<void>;
  handleEditGoal: (goal: ApiGoal) => void;
  handleDetailsGoal: (goal: ApiGoal) => void;
  handleDeleteGoal: (goal: ApiGoal) => void;
}

export function useGoalsTableHook(goals: ApiGoal[]): UseGoalsTableStateResult {
  const { t } = useTranslation();
  const modalsService = useModals();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const goalDeleteMutation = useGoalDelete();

  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
  const [childrenMap, setChildrenMap] = useState<Record<number, ApiGoal[]>>({});
  const [loadingPaths, setLoadingPaths] = useState<Set<string>>(new Set());

  const toggleExpand = useCallback(
    async (goalId: number, path: string) => {
      if (expandedPaths.has(path)) {
        setExpandedPaths((prev) => {
          const next = new Set(prev);
          next.delete(path);
          return next;
        });
      } else {
        if (!childrenMap[goalId]) {
          setLoadingPaths((prev) => new Set(prev).add(path));
          try {
            const data = await queryClient.fetchQuery(
              goalsKeys.search({ parent_goal_id: [goalId] } as ApiGoalSearchFilter)
            );
            setChildrenMap((prev) => ({ ...prev, [goalId]: data?.items ?? [] }));
          } finally {
            setLoadingPaths((prev) => {
              const next = new Set(prev);
              next.delete(path);
              return next;
            });
          }
        }
        setExpandedPaths((prev) => new Set(prev).add(path));
      }
    },
    [expandedPaths, childrenMap, queryClient]
  );

  const flattenGoals = useCallback(
    function flattenGoals(items: ApiGoal[], depth = 0, parentPath = "root"): FlatGoalRow[] {
      const result: FlatGoalRow[] = [];
      for (const goal of items) {
        const path = `${parentPath}-${goal.id}`;
        result.push({ goal, depth, path });
        if (expandedPaths.has(path) && childrenMap[goal.id]) {
          result.push(...flattenGoals(childrenMap[goal.id], depth + 1, path));
        }
      }
      return result;
    },
    [expandedPaths, childrenMap]
  );

  const flatGoals = useMemo(() => flattenGoals(goals), [goals, flattenGoals]);

  const handleEditGoal = useCallback(
    (goal: ApiGoal) => {
      modalsService.openModal(GoalEditModal, { goalId: goal.id });
    },
    [modalsService]
  );

  const handleDetailsGoal = useCallback(
    (goal: ApiGoal) => {
      navigate(`/goals/${goal.id}`);
    },
    [navigate]
  );

  const handleDeleteGoal = useCallback(
    (goal: ApiGoal) => {
      const onDeleted = () => {
        setChildrenMap((prev) => {
          const next = { ...prev };
          for (const parentId in next) {
            next[parentId] = next[parentId].filter((g) => g.id !== goal.id);
          }
          delete next[goal.id];
          return next;
        });

        analyticsService.trackEvent(analyticEvents.goals.goalDeleted, {
          [analyticProperties.id]: goal.id,
        });
      };

      if (goal.has_children) {
        modalsService.openModal(GoalConfirmationDeleteWithSubGoalsModal, {
          goalId: goal.id,
          onDeleted,
        });
      } else {
        modalsService.openModal(ConfirmationModal, {
          question: t("goals:delete_goal"),
          text: t("goals:delete_goal_confirmation"),
          acceptTitle: t("common:delete"),
          onAccept: async () => {
            await goalDeleteMutation.mutateAsync({ id: goal.id });
            onDeleted();
          },
        });
      }
    },
    [modalsService, goalDeleteMutation, t]
  );

  return {
    expandedPaths,
    loadingPaths,
    childrenMap,
    flatGoals,
    toggleExpand,
    handleEditGoal,
    handleDetailsGoal,
    handleDeleteGoal,
  };
}
